import * as ex from 'excalibur';
import { ActionType, EntityType, PlantType } from '../types/enums';
import { Action, PlantConfig } from '../types/interfaces';
import { TILE_SIZE } from '../config/constants';
import { Entity } from './entity';
import { Grid } from './grid';

export class Plant extends Entity {
  public plantType: PlantType;
  public growthStage: number;
  public maxGrowthStage: number;
  public growthRate: number;
  public interactable: boolean;
  private lastGrowthTime: number = 0;

  constructor(options: PlantConfig & { grid: Grid }) {
    super({
      ...options,
      type: EntityType.Plant,
      grid: options.grid
    });

    this.plantType = options.plantType;
    this.growthStage = options.growthStage;
    this.maxGrowthStage = options.maxGrowthStage;
    this.growthRate = options.growthRate;
    this.interactable = options.interactable;
    
    // Set a default emoji based on plant type (can be overridden)
    this.setPlantEmoji();
    
    // Adjust the size based on growth stage
    this.updateVisualSize();
  }
  
  // Set appropriate emoji based on plant type
  private setPlantEmoji() {
    switch (this.plantType) {
      case PlantType.Tree:
        this.setEmoji('🌳');
        break;
      case PlantType.Flower:
        this.setEmoji('🌸');
        break;
      case PlantType.Bush:
        this.setEmoji('🌿');
        break;
      case PlantType.Grass:
        this.setEmoji('🌱');
        break;
      default:
        this.setEmoji('🌱');
    }
  }
  
  // Update visual appearance based on growth stage
  private updateVisualSize() {
    // Scale from 0.5 to 1.0 based on growth stage
    const scale = 0.5 + (0.5 * this.growthStage / this.maxGrowthStage);
    
    // Update the scale
    this.scale = new ex.Vector(scale, scale);
    
    // If we have a sprite, update its scale too
    if (this.sprite) {
      this.sprite.scale = new ex.Vector(scale, scale);
      // Re-apply the sprite to update the graphics
      this.graphics.use(this.sprite);
    }
  }

  // Check if it's time to grow
  public canGrow(currentTime: number): boolean {
    return this.growthStage < this.maxGrowthStage && 
           (currentTime - this.lastGrowthTime >= this.growthRate);
  }

  // Called each frame to decide next action
  public getNextAction(): Action | null {
    // Plants mostly don't do anything but grow
    const currentTime = Date.now();
    
    // Check if we should grow
    if (this.canGrow(currentTime)) {
      return {
        type: ActionType.Wait, // We use wait but it's actually growth
        entity: this,
        execute: () => {
          this.growthStage++;
          this.lastGrowthTime = currentTime;
          this.updateVisualSize();
          console.log(`${this.plantType} grows to stage ${this.growthStage}/${this.maxGrowthStage}`);
        }
      };
    }
    
    return null;
  }
  
  // Interact with the plant (for animals or player)
  public interact(entity: Entity): void {
    if (!this.interactable) return;
    
    console.log(`Entity interacts with ${this.plantType}`);
    
    // Different interactions based on plant type
    switch (this.plantType) {
      case PlantType.Tree:
        // Trees could drop fruit
        break;
      case PlantType.Flower:
        // Flowers could provide pollen
        break;
      case PlantType.Bush:
        // Bushes could provide berries
        break;
      case PlantType.Grass:
        // Grass could be eaten
        this.growthStage = Math.max(0, this.growthStage - 1);
        this.updateVisualSize();
        break;
    }
  }
}